import styled, { css } from "styled-components";
import { media } from "../../theme/theme";
import { AuthoryColorSchemeProps, CustomDarkShade, getFontColor, UserThemableHeading, UserThemableParagraph } from "./FontSetUtilities";

const BlockQuoteCSS = css`
    display: block;
    margin-block-start: 1em;
    margin-block-end: 1em;
    margin-inline-start: 40px;
    margin-inline-end: 40px;
    border-left: ${({ theme }) => (theme ? `2px solid ${theme.customization.accentcolour}` : null)};
    margin-left: 10px;
    padding-left: 20px;
    font-size: 20px;
    line-height: 30px;
    font-family: "Lora", serif;
    ${UserThemableParagraph};
    margin-top: 36px;
    margin-bottom: 36px;
`;

export const V3ArticleView = styled.article<AuthoryColorSchemeProps>`
    line-height: 1.5;
    word-break: break-word;
    table {
        width: 100%;
        border-collapse: collapse;
    }

    table td {
        border: 1px solid black;
        padding: 5px;
        height: 25px;
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
        display: block;
        margin-block-start: 0.83em;
        margin-block-end: 0.83em;
        ${UserThemableHeading}
        font-weight: 400;
        letter-spacing: -1px;

        /* overide color for paraphs */
        color: ${({ theme, $colorVariant, $customBackgroundColor }) =>
            getFontColor($customBackgroundColor || theme.customization.backgroundColor, $colorVariant, CustomDarkShade.BlackPearl)};

        &:first-child {
            margin-top: 0;
        }

        b,
        strong {
            font-weight: normal;
        }

        em {
            font-style: initial;
        }
    }

    b,
    strong,
    em {
        h1,
        h2,
        h3,
        h4,
        h5,
        h6 {
            font-weight: normal;
            font-style: initial;
        }
    }

    h1 {
        font-size: 24px;
        line-height: 32px;

        ${media.phone} {
            font-size: 36px;
            line-height: 1.3em;
        }

        @media print {
            page-break-inside: avoid;

            &:after {
                content: "";
                display: block;
                height: 100px;
                margin-bottom: -100px;
            }
        }
    }

    h2 {
        font-size: 22px;
        line-height: 28px;
        margin-top: 48px;
        margin-bottom: 12px;

        ${media.phone} {
            font-size: 28px;
            line-height: 36px;
        }

        @media print {
            page-break-inside: avoid;

            &:after {
                content: "";
                display: block;
                height: 100px;
                margin-bottom: -100px;
            }
        }
    }

    h3,
    h4,
    h5,
    h6 {
        font-size: 24px;
        line-height: 30px;
        margin-top: 48px;
        margin-bottom: 12px;

        @media print {
            page-break-inside: avoid;

            &:after {
                content: "";
                display: block;
                height: 100px;
                margin-bottom: -100px;
            }
        }
    }

    .ql-bubble .ql-editor blockquote,
    blockquote {
        ${BlockQuoteCSS}
        /* overide color for paraphs */
        color: ${({ theme, $colorVariant, $customBackgroundColor }) =>
            getFontColor($customBackgroundColor || theme.customization.backgroundColor, $colorVariant, CustomDarkShade.BlackPearl1)};
    }

    div,
    p,
    ol,
    ul,
    li,
    table {
        font-size: 17px;
        line-height: 27px;
        ${UserThemableParagraph}

        /* overide color for paraphs */
        color: ${({ theme, $colorVariant, $customBackgroundColor }) =>
            getFontColor($customBackgroundColor || theme.customization.backgroundColor, $colorVariant, CustomDarkShade.BlackPearl1)};

        ${media.phone} {
            font-size: 19px;
            line-height: 31px;
        }

        @media print {
            font-size: 16px;
            line-height: 30px;
        }
    }

    .ql-bubble .ql-editor a,
    a {
        color: #2e77ff;
        text-decoration: none;
    }

    a:hover {
        color: #0050ee;
    }

    p + p {
        margin-top: 1.625rem;
    }

    img {
        max-width: 100%;
    }

    figure {
        text-align: center;
        margin: 1em auto;
    }

    video {
        max-width: 100%;
    }

    @media screen and (max-width: 600px) {
        /* Removes image margin left/right on mobile devices */
        figure {
            margin-left: 0px;
            margin-right: 0px;
        }
    }

    figcaption {
        ${UserThemableParagraph}
        font-size: 16px;
        text-align: center;
        font-family: "Times New Roman";
    }

    li {
        ${UserThemableParagraph}
        /* overide color for paraphs */
        color: ${({ theme, $colorVariant, $customBackgroundColor }) =>
            getFontColor($customBackgroundColor || theme.customization.backgroundColor, $colorVariant, CustomDarkShade.BlackPearl1)};

        p {
            display: inline-block;
        }
    }

    b,
    strong {
        font-weight: bolder;
    }

    em {
        font-style: italic;
    }

    .ql-bubble .ql-editor code,
    pre,
    code {
        margin-top: 1.625rem;
        margin-bottom: 1.625rem;
        font-family: monospace, monospace;
        font-size: 16px;
        line-height: 24px;
        padding: 0;
        background: none;
        font-size: 16px;
        max-width: 100%;
        overflow: scroll;
        background-color: none;
        ${UserThemableParagraph}
        font-family: monospace;
    }

    ol,
    ul {
        display: block;
        margin-block-start: 1em;
        margin-block-end: 1em;
        margin-inline-start: 0px;
        margin-inline-end: 0px;
        padding-left: 1.5em;
    }

    ol li:not(.ql-direction-rtl),
    ul li:not(.ql-direction-rtl) {
        padding-left: 1.5em;
    }

    ol li:not(.ql-direction-rtl),
    ul li:not(.ql-direction-rtl) {
        padding-left: 1.5em;
    }

    ol li.ql-direction-rtl,
    ul li.ql-direction-rtl {
        padding-right: 1.5em;
    }
    ol li {
        counter-reset: list-1 list-2 list-3 list-4 list-5 list-6 list-7 list-8 list-9;
        counter-increment: list-0;

        p {
            display: inline;
        }
    }
    ol li:before {
        content: counter(list-0, decimal) ". ";
    }
    ol li.ql-indent-1 {
        counter-increment: list-1;
    }
    ol li.ql-indent-1:before {
        content: counter(list-1, lower-alpha) ". ";
    }
    ol li.ql-indent-1 {
        counter-reset: list-2 list-3 list-4 list-5 list-6 list-7 list-8 list-9;
    }
    ol li.ql-indent-2 {
        counter-increment: list-2;
    }
    ol li.ql-indent-2:before {
        content: counter(list-2, lower-roman) ". ";
    }
    ol li.ql-indent-2 {
        counter-reset: list-3 list-4 list-5 list-6 list-7 list-8 list-9;
    }
    ol li.ql-indent-3 {
        counter-increment: list-3;
    }
    ol li.ql-indent-3:before {
        content: counter(list-3, decimal) ". ";
    }
    ol li.ql-indent-3 {
        counter-reset: list-4 list-5 list-6 list-7 list-8 list-9;
    }

    li.ql-indent-1:not(.ql-direction-rtl) {
        padding-left: 3em;
    }

    li.ql-indent-2:not(.ql-direction-rtl) {
        padding-left: 4.5em;
    }

    li:not(.ql-direction-rtl)::before {
        margin-left: -1.5em;
        margin-right: 0.3em;
        text-align: right;
    }

    li::before {
        display: inline-block;
        white-space: nowrap;
        width: 1.2em;
    }

    ul li:before {
        content: "•";
    }

    ul li p {
        display: inline;
    }

    iframe {
        opacity: 0;
        transition-delay: 150ms;
    }

    .iframe-wrapper {
        margin: 20px auto;
        position: relative;
        overflow: hidden;
        width: 100%;

        iframe {
            position: absolute;
            top: 0;
            left: 0;
            bottom: 0;
            right: 0;
            width: 100%;
            height: 100%;
            opacity: 1;
        }
    }

    .iframe-youtube {
        padding-top: 56.25%;
    }

    .iframe-instagram {
        padding-top: 83%;
    }

    .iframe-instagram-reel {
        width: 70%;
        padding-top: 117%;
    }
`;
